import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useCreateResponse, useUpdatePageBookmark } from "../../api/mutations";
import {
  useGetPage,
  useGetPageBookmarks,
  useGetPageContainedLessons,
  useGetLesson,
} from "../../api/queries";
import Button from "../../componentLibrary/Button";
import Modal from "../../componentLibrary/Modal";
import Spinner from "../../componentLibrary/Spinner";
import { useNotification } from "../../hooks/useNotification";
import { ComponentEnum, ResponseEnum, BookmarkI } from "../../types";
import { ForEachComponentRef } from "../CreateLessonPage/components/contentComponents/ForEachComponent";
import { PriorityListRef } from "../CreateLessonPage/components/contentComponents/PriorityListComponent";
import { parsePreviousAnswers } from "../CreateLessonPage/utils/parsePreviousAnswers";
import { renderPageComponents } from "../CreateLessonPage/utils/renderComponent";
import EditChoicesModal from "./EditChoicesModal";

interface ActivityModalProps {
  pageId: string;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  moduleId?: string;
  lessonId?: string;
}

export default function ActivityModal({
  pageId,
  isModalOpen,
  setIsModalOpen,
  moduleId,
  lessonId,
}: ActivityModalProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [activityResponse, setActivityResponse] = useState<Record<string, any>>(
    {},
  );
  const [isEditChoicesModalOpen, setIsEditChoicesModalOpen] = useState(false);
  const lastChangedVariable = useRef<string | null>(null);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { data: page, isError, isLoading } = useGetPage(pageId);
  const { data: pageBookmarks = [] } = useGetPageBookmarks(pageId, moduleId);
  const { data: lesson } = useGetLesson(lessonId || "");
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  // Get lessons that contain this page (only if lessonId not provided)
  const { data: lessons } = useGetPageContainedLessons(pageId, {
    enabled: !lessonId, // Only fetch if lessonId is not provided
  });

  // Storing PriorityList component refs to call function present inside the component
  const priorityListRefs = useRef<Record<string, PriorityListRef | null>>({});

  // Create refs for components
  const forEachRefs = useRef<Record<string, ForEachComponentRef | null>>({});

  const relevantBookmark: BookmarkI | undefined = pageBookmarks[0];

  const { frozenComponents = [], frozenAnswers } = relevantBookmark || {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resetResponses = (frozenAnswers: Record<string, any>) => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    if (frozenAnswers) {
      setActivityResponse(frozenAnswers);
      syncTimeoutRef.current = setTimeout(() => {
        Object.keys(frozenAnswers).forEach((key) => {
          synchForEach(key, frozenAnswers);
          synchPriorityList(key, frozenAnswers);
        });
        syncTimeoutRef.current = null;
      }, 210);
    } else {
      setActivityResponse({});
    }
  };

  useEffect(() => {
    resetResponses(frozenAnswers);
  }, [frozenAnswers]);

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, []);

  const synchForEach = (
    variable: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    answers: Record<string, any>,
  ) => {
    Object.entries(forEachRefs.current).forEach(([, ref]) => {
      if (ref && typeof ref.syncVariableChange === "function") {
        ref.syncVariableChange(variable, answers);
      }
    });
  };

  const synchPriorityList = (
    variable: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    answers: Record<string, any>,
  ) => {
    Object.entries(priorityListRefs.current).forEach(([, ref]) => {
      if (ref && typeof ref.syncVariableChange === "function") {
        ref.syncVariableChange(variable, answers);
      }
    });
  };

  // Synchronize components after state updates
  useEffect(() => {
    if (lastChangedVariable.current) {
      synchForEach(lastChangedVariable.current, activityResponse);
      synchPriorityList(lastChangedVariable.current, activityResponse);
      lastChangedVariable.current = null;
    }
  }, [activityResponse]);

  const updateFrozenAnswersInBookmark = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    responseToUse?: Record<string, any>,
  ) => {
    if (!relevantBookmark) {
      return;
    }

    const frozenVariables = page?.freezeVariablesFromPreviousPage || [];

    if (frozenVariables.length === 0) {
      return;
    }

    // Use provided response or fall back to current activityResponse
    const response = responseToUse || activityResponse;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updatedFrozenAnswers: Record<string, any> = {};

    frozenVariables.forEach((key) => {
      if (response[key] !== undefined) {
        updatedFrozenAnswers[key] = response[key];
      }
    });

    updateBookmarkMutation.mutate({
      ...relevantBookmark,
      _id: relevantBookmark._id,
      module: relevantBookmark.module?._id,
      lesson: relevantBookmark.lesson?._id,
      page: relevantBookmark.page?._id,
      type: relevantBookmark.type,
      frozenAnswers: updatedFrozenAnswers,
    });
  };

  const handleSave = () => {
    if (!page) {
      return;
    }

    // Use provided lessonId or fall back to first lesson from fetched lessons
    const lessonIdToUse =
      lessonId || (lessons && lessons.length > 0 ? lessons[0]._id : null);

    if (!lessonIdToUse) {
      return;
    }

    const responseData = {
      module: moduleId,
      lesson: lessonIdToUse,
      page: pageId,
      title: page.title,
      keyTakeaway:
        page.keyTakeaways && page.keyTakeaways.trim() !== ""
          ? page.keyTakeaways
          : undefined,
      type: ResponseEnum.ACTIVITY,
      content: processResponseForSaving(activityResponse),
    };

    // Update frozen answers in bookmark
    updateFrozenAnswersInBookmark();

    mutation.mutate(responseData);
    setIsModalOpen(false);
    resetResponses(frozenAnswers);
  };

  const updateBookmarkMutation = useUpdatePageBookmark({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
    },
    onError: () => {
      notify("error", "Failed to update frozen answers");
    },
  });

  const mutation = useCreateResponse({
    onSuccess: async () => {
      resetResponses(frozenAnswers);
      // Invalidate multiple query keys to ensure journal updates
      await queryClient.invalidateQueries({ queryKey: ["responses"] });
      await queryClient.invalidateQueries({
        queryKey: ["responses", "grouped"],
      });
      await queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      notify("success", "Your response has been saved to your journal!");
    },
    onError: () => {
      notify("error", "There was an error saving your response.");
    },
  });

  const handleChange = (
    variable: string,
    label: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    displayAs?: string,
  ) => {
    lastChangedVariable.current = variable;
    setActivityResponse((prev) => {
      const newState = { ...prev };
      if (value === undefined) {
        delete newState[variable];
      } else {
        newState[variable] = {
          label: label,
          value,
          displayAs,
        };
      }
      return newState;
    });
  };

  //Function to process the response for saving in the database
  const processResponseForSaving = (
    response: Record<
      string,
      { label: string; value: string | string[]; displayAs?: string }
    >,
  ) => {
    const processedResponse: Record<
      string,
      { label: string; value: string | string[]; displayAs?: string }
    > = {};

    const frozenVariables = page?.freezeVariablesFromPreviousPage || [];

    for (const [key, entry] of Object.entries(response)) {
      // Skip if the key is in the frozen variables list
      if (frozenVariables.includes(key)) {
        continue;
      }

      if (Array.isArray(entry.value)) {
        // Process each value in the array
        processedResponse[key] = {
          label: parsePreviousAnswers(entry.label, response),
          value: entry.value.map((val) => parsePreviousAnswers(val, response)),
          displayAs: entry.displayAs,
        };
      } else {
        // Process single value
        processedResponse[key] = {
          label: parsePreviousAnswers(entry.label, response),
          value: parsePreviousAnswers(entry.value, response),
          displayAs: entry.displayAs,
        };
      }
    }

    return processedResponse;
  };

  if (isLoading) return <Spinner className="mt-4 mx-auto" />;
  if (isError || !page) return <div>Error</div>;

  const frozenVariables = page?.freezeVariablesFromPreviousPage || [];
  const hasAnswers = Object.keys(activityResponse).some(
    (key) => !frozenVariables.includes(key),
  );

  // Build lesson page URL for calendar backlink (when opened from toolbox)
  const lessonPageIndex =
    lesson?.pages?.findIndex((p) => p._id === pageId) ?? -1;
  const lessonPageUrl =
    moduleId && lessonId && lesson && lessonPageIndex >= 0
      ? `${window.location.origin}/topics/${moduleId}/sections/${lessonId}?page=${lessonPageIndex}`
      : undefined;
  const toolboxUrlForCalendar = moduleId
    ? `${window.location.origin}/my-toolboxes/${moduleId}`
    : undefined;

  const renderOptions = {
    ALL: {
      savePageInput: handleSave,
    },
    [ComponentEnum.FOR_EACH]: {
      onRefAssign: (componentId: string, ref: ForEachComponentRef | null) => {
        forEachRefs.current[componentId] = ref;
      },
    },
    [ComponentEnum.PRIORITY_LIST]: {
      onRefAssign: (componentId: string, ref: PriorityListRef | null) => {
        priorityListRefs.current[componentId] = ref;
      },
    },
    [ComponentEnum.ADD_TO_CALENDAR]: {
      lessonPageUrl: lessonPageUrl,
      toolboxUrl: toolboxUrlForCalendar,
      isPageInToolbox: true,
    },
  };

  return (
    <Modal
      title={page.title}
      size="xl"
      height="90vh"
      isOpen={isModalOpen}
      isCloseButtonVisible={false}
      onCloseModal={() => {
        setIsModalOpen(false);
        resetResponses(frozenAnswers);
      }}
    >
      <div className="flex flex-col h-full relative">
        <div className="flex-1 overflow-y-auto p-0 md:p-10 lg:p-12 pb-20 mt-6 mb-8 space-y-6 flex-grow">
          {renderPageComponents(
            page.components,
            0,
            activityResponse,
            activityResponse,
            handleChange,
            renderOptions,
            "textarea",
          )}
        </div>

        <div className="absolute bottom-0 w-full bg-white border-t border-gray-200 p-4">
          <div className="flex flex-row gap-2 justify-center">
            {frozenComponents.length > 0 && (
              <Button
                className="px-6 py-2"
                variant="secondary"
                onClick={() => setIsEditChoicesModalOpen(true)}
              >
                Edit My Choices
              </Button>
            )}
            {hasAnswers ? (
              <>
                <Button className="px-6 py-2" onClick={handleSave}>
                  Save and Close
                </Button>
                <Button
                  className="px-6 py-2"
                  variant="cautionary"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetResponses(frozenAnswers);
                  }}
                >
                  Close without Saving
                </Button>
              </>
            ) : (
              <Button
                className="px-8 py-2"
                variant="secondary"
                onClick={() => {
                  setIsModalOpen(false);
                  resetResponses(frozenAnswers);
                }}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </div>

      <EditChoicesModal
        isOpen={isEditChoicesModalOpen}
        onClose={() => setIsEditChoicesModalOpen(false)}
        onSave={(tempResponse) => {
          setActivityResponse((prev) => ({ ...prev, ...tempResponse }));
          updateFrozenAnswersInBookmark(tempResponse);
          setIsEditChoicesModalOpen(false);
        }}
        frozenComponents={frozenComponents}
        frozenAnswers={frozenAnswers || {}}
        renderOptions={renderOptions}
      />
    </Modal>
  );
}
